const REQUIRED_KEYS = [
  'msalClientId',
  'msalTenantId',
  'msalRedirectUri',
  'msalPostLogoutRedirectUri',
  'apiApplicationIdUri',
];

const developmentConfig = () => {
  if (process.env.NODE_ENV === 'production') return null;

  const clientId = process.env.REACT_APP_MSAL_CLIENT_ID || process.env.SP_CLIENT_ID;
  const tenantId = process.env.REACT_APP_MSAL_TENANT_ID || process.env.SP_TENANT_ID;
  const config = {
    msalClientId: clientId,
    msalTenantId: tenantId,
    msalRedirectUri: process.env.REACT_APP_MSAL_REDIRECT_URI,
    msalPostLogoutRedirectUri: process.env.REACT_APP_MSAL_POST_LOGOUT_REDIRECT_URI,
    apiApplicationIdUri: process.env.REACT_APP_MSAL_API_APP_ID_URI ||
      (clientId ? `api://${clientId}` : undefined),
    offerteAnnotatorEnabled: process.env.REACT_APP_OFFERTE_ANNOTATOR_ENABLED,
  };

  return REQUIRED_KEYS.every((key) => typeof config[key] === 'string' && config[key].trim())
    ? config
    : null;
};

export const validateRuntimeConfig = (config) => {
  if (!config || typeof config !== 'object' || Array.isArray(config)) {
    throw new Error('Runtime configuration is invalid: expected an object');
  }

  const missing = REQUIRED_KEYS.filter((key) => config[key] === undefined || config[key] === null || config[key] === '');
  if (missing.length) {
    throw new Error(`Runtime configuration is missing: ${missing.join(', ')}`);
  }

  const malformed = REQUIRED_KEYS.filter(
    (key) => typeof config[key] !== 'string' || !config[key].trim()
  );
  if (malformed.length) {
    throw new Error(`Runtime configuration is invalid: ${malformed.join(', ')}`);
  }

  if (config.offerteAnnotatorEnabled !== undefined && typeof config.offerteAnnotatorEnabled !== 'string') {
    throw new Error('Runtime configuration is invalid: offerteAnnotatorEnabled');
  }

  return config;
};

export const setRuntimeConfig = (config) => {
  const validated = validateRuntimeConfig(config);
  window.__SDB_RUNTIME_CONFIG__ = validated;
  return validated;
};

export const getRuntimeConfig = () => {
  if (typeof window !== 'undefined' && window.__SDB_RUNTIME_CONFIG__) {
    return validateRuntimeConfig(window.__SDB_RUNTIME_CONFIG__);
  }
  return developmentConfig();
};

// Per-deployment switch for the offerte annotator (App Setting OFFERTE_ANNOTATOR_ENABLED,
// served through /config.json). Every /config.json value is a string, so the flag is on only
// for the exact string "true"; anything else, or no key at all, is off.
export const isOfferteAnnotatorEnabled = () => getRuntimeConfig()?.offerteAnnotatorEnabled === 'true';

export const loadRuntimeConfig = async (fetchImpl = window.fetch.bind(window)) => {
  try {
    const response = await fetchImpl('/config.json', { cache: 'no-store' });
    if (!response.ok) {
      throw new Error(`Failed to load runtime configuration: ${response.status}`);
    }
    return validateRuntimeConfig(await response.json());
  } catch (error) {
    // CRA's development server does not provide /config.json. Local development continues
    // to use .env.local, while production remains fail-closed and visibly reports the error.
    const fallback = process.env.NODE_ENV === 'development' ? developmentConfig() : null;
    if (fallback) return fallback;
    throw error;
  }
};
